/**
 * AssistantSidebar Component
 * Main container for the AI pipeline assistant
 */

import React, { useState } from 'react';
import { X, Maximize2, Minimize2, Trash2 } from 'lucide-react';
import { ChatTimeline } from './ChatTimeline';
import { MessageInput } from './MessageInput';
import { ConnectionStatus } from './ConnectionStatus';
import { useAssistant } from '../hooks';

interface AssistantSidebarProps {
  pipelineId: number;
  onClose: () => void;
}

export const AssistantSidebar: React.FC<AssistantSidebarProps> = ({
  pipelineId,
  onClose,
}) => {
  // State
  const [width, setWidth] = useState(400);
  const [isExpanded, setIsExpanded] = useState(false);

  // Connect to assistant
  const {
    isConnected,
    connectionState,
    error,
    timeline,
    streamingMessage,
    isStreaming,
    sendMessage,
    stopStreaming,
    clearConversation,
  } = useAssistant({ pipelineId});

  // Toggle expanded mode
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Handle clear conversation with confirmation
  const handleClearConversation = async () => {
    if (window.confirm('Are you sure you want to clear the conversation? This cannot be undone.')) {
      await clearConversation();
    }
  };

  return (
    <div
      className={`fixed right-0 top-0 h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-xl flex flex-col z-50 transition-all duration-300 ${
        isExpanded ? 'w-1/2' : ''
      }`}
      style={!isExpanded ? { width: `${width}px` } : undefined}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3 flex-1">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Pipeline Assistant
          </h2>
          <ConnectionStatus connectionState={connectionState} error={error} />
        </div>

        <div className="flex items-center gap-2">
          {/* Clear conversation button */}
          <button
            onClick={handleClearConversation}
            disabled={!isConnected || timeline.length === 0}
            className="p-1.5 rounded-md text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Clear conversation"
          >
            <Trash2 className="h-5 w-5" />
          </button>

          {/* Expand/Collapse button */}
          <button
            onClick={toggleExpanded}
            className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <Minimize2 className="h-5 w-5" />
            ) : (
              <Maximize2 className="h-5 w-5" />
            )}
          </button>

          {/* Close button */}
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Chat Timeline */}
      <div className="flex-1 overflow-y-auto">
        <ChatTimeline
          timeline={timeline}
          streamingMessage={streamingMessage}
          isStreaming={isStreaming}
          error={error}
        />
      </div>

      {/* Message Input */}
      <div className="border-t border-gray-200 dark:border-gray-700">
        <MessageInput
          onSend={sendMessage}
          onStop={stopStreaming}
          isStreaming={isStreaming}
          disabled={!isConnected}
          placeholder={
            !isConnected
              ? 'Connecting...'
              : 'Ask me to help build your pipeline...'
          }
        />
      </div>

      {/* Resize Handle (only when not expanded) */}
      {!isExpanded && (
        <div
          className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500 transition-colors"
          onMouseDown={(e) => {
            e.preventDefault();
            const startX = e.clientX;
            const startWidth = width;

            const handleMouseMove = (moveEvent: MouseEvent) => {
              const deltaX = startX - moveEvent.clientX;
              const newWidth = Math.max(300, Math.min(800, startWidth + deltaX));
              setWidth(newWidth);
            };

            const handleMouseUp = () => {
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
        />
      )}
    </div>
  );
};
